import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { Icon, LinkButton } from '@evertrue/et-components';
import { getQueryOptions } from 'apps/layout/global-search/config/global-search-config';
import { useGate } from 'components/is-gated';
import { useGlobalSearch } from 'apps/layout/global-search/global-search-context';

const GlobalSearchBox = ({
  className = '',
  searchRef = null,
  name = '',
  defaultValue = '',
  label = '',
  onKeyUp = () => {},
  onChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  queryType = 'all',
  setQueryType = () => {},
  placeholder = '',
}) => {
  const isMountedRef = useRef(false);
  const prevDefaultValueRef = useRef(defaultValue);
  const hasInteractions = useGate('ugc_show_imported_notes');
  const QUERY_OPTIONS = getQueryOptions(hasInteractions);

  const { clearSearch } = useGlobalSearch();

  useEffect(() => {
    isMountedRef.current = true;
    prevDefaultValueRef.current = defaultValue;

    return () => {
      isMountedRef.current = false;
    };
  }, [defaultValue, searchRef]);

  const handleClear = (e) => {
    clearSearch();
    if (searchRef.current) {
      searchRef.current.value = '';
    }
    if (e) {
      handleKeyUp(e);
      handleChange(e);
    }
    focusOnCancelIcon();
  };

  const handleKeyUp = (e) => {
    if (_.isFunction(onKeyUp)) {
      onKeyUp(searchRef.current?.value, e);
    }
  };

  const handleChange = (e) => {
    if (_.isFunction(onChange)) {
      onChange(searchRef.current?.value, e);
    }
  };

  const focusOnCancelIcon = () => {
    const cancelIcon = document.querySelector('.search-box--clear');
    cancelIcon?.focus();
  };

  const focusIcon = (e) => {
    e.preventDefault();
    e.target.focus();
  };

  const searchPlaceholder = !placeholder ? QUERY_OPTIONS[queryType].placeholder : placeholder;

  return (
    <div className={classNames('global-search-box-container', className)}>
      <span className={'global-search-box'}>
        <input
          aria-label={searchPlaceholder || name}
          type="search"
          ref={searchRef}
          className="global-search-box--input" // Used in jasmine tests
          label={label || searchPlaceholder}
          name={name}
          defaultValue={defaultValue}
          placeholder={searchPlaceholder}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyUp={handleKeyUp}
          onChange={handleChange}
        />

        {searchRef.current?.value && (
          <LinkButton title="Clear search box" onClick={handleClear} tabIndex={-1}>
            <Icon
              className="search-box--clear"
              icon="cancel"
              tabIndex={0}
              role="button"
              ariaLabel="Clear search"
              onFocus={focusIcon}
            />
          </LinkButton>
        )}
      </span>
    </div>
  );
};

export default GlobalSearchBox;

GlobalSearchBox.propTypes = {
  queryType: PropTypes.string,
  setQueryType: PropTypes.func,
  className: PropTypes.string,
  searchRef: PropTypes.object,
  name: PropTypes.string,
  defaultValue: PropTypes.any,
  label: PropTypes.string,
  onKeyUp: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
};
